// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@carbon/icons-react';
import { useCreatePolicy, isPolicyValid } from '../../shared/procedures';
import PolicyForm from '../components/PolicyForm';
import ProcedureFormFooter from '../components/ProcedureFormFooter';
import PolicyPreview from '../components/PolicyPreview';
import ProcedureJsonPane from '../components/ProcedureJsonPane';
import StatusTag from '../components/StatusTag';
import type { PolicyDetail } from '../../shared/types/procedures';
// Same page layout as ProcedureViewPage/CreateProcedurePage (breadcrumb,
// tabs, panes, footer) — reusing its CSS module rather than duplicating it.
import styles from './ProcedureViewPage.module.css';

function blankPolicy(): PolicyDetail {
  return {
    artifactId: '',
    artifactType: 'policy',
    title: '',
    policyNumber: '',
    version: '0.1',
    status: 'draft',
    classification: '',
    policyDomain: '',
    scope: '',
    enforcementPoint: '',
    decisionSummary: '',
    package: '',
    files: { policy: '', data: '', tests: '' },
  };
}

/**
 * "Start a blank policy" screen — route `procedures/new-policy`. Same
 * Guided / Live Preview / Advanced-JSON layout as `CreateProcedurePage`,
 * seeded with a blank policy-as-code draft instead of a procedure one.
 */
export default function CreatePolicyPage() {
  const navigate = useNavigate();
  const { create, isCreating, error: createError } = useCreatePolicy();
  const [blank] = useState(blankPolicy);
  const [policy, setPolicy] = useState<PolicyDetail>(blank);
  const [tab, setTab] = useState<0 | 1>(0);

  // `ProcedureFormFooter`'s Patch/Minor bump is for revising an *existing*
  // approved version — meaningless on a brand-new v0.1 draft, so the version
  // it computes is ignored here in favor of the draft's own version field.
  async function handleSubmit() {
    try {
      await create(policy);
      navigate('/admin-center/procedures/library');
    } catch {
      // Surfaced via `createError` in the footer — stay on the page so
      // nothing typed gets lost.
    }
  }

  return (
    <div className={styles.page}>
      <button
        type="button"
        className={styles.backLink}
        onClick={() => navigate('/admin-center/procedures/library')}
      >
        <ArrowLeft size={16} /> Back to Library
      </button>

      <div className={styles.titleRow}>
        <h1 className={styles.title}>New Policy</h1>
        <div className={styles.switcher} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 0}
            className={`${styles.switchBtn} ${tab === 0 ? styles.switchBtnSelected : ''}`}
            onClick={() => setTab(0)}
          >
            Guided
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 1}
            className={`${styles.switchBtn} ${tab === 1 ? styles.switchBtnSelected : ''}`}
            onClick={() => setTab(1)}
          >
            Advanced: raw JSON
          </button>
        </div>
      </div>

      <div className={styles.subtitle}>
        <span>{policy.artifactType.toUpperCase()}</span>
        <span aria-hidden="true">·</span>
        <StatusTag status={policy.status} />
      </div>

      <div className={styles.panes}>
        <div className={styles.pane}>
          <div className={styles.paneScroll}>
            {tab === 0 ? (
              <PolicyForm policy={policy} original={blank} onChange={setPolicy} hideChanges />
            ) : (
              <ProcedureJsonPane procedure={policy} />
            )}
          </div>
          <ProcedureFormFooter
            version={policy.version}
            onCancel={() => navigate('/admin-center/procedures/library')}
            onSubmit={handleSubmit}
            isSaving={isCreating}
            canSubmit={isPolicyValid(policy)}
            error={createError}
          />
        </div>
        <div className={`${styles.pane} ${styles.panePreview}`}>
          <div className={styles.paneScroll}>
            <PolicyPreview policy={policy} />
          </div>
        </div>
      </div>
    </div>
  );
}
